import { apiClient } from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import type {
  WorkPolicyCreateInput,
  WorkPolicyDetails,
  WorkPolicyListFilters,
  WorkPolicyListItem,
  WorkPolicySearchResult,
  WorkPolicyUpdateInput,
} from '../types/workPolicy';
import { CONFIGURATION_RECORD_INACTIVE } from '../constants/configuration';
import { nextDisplayOrder } from '../utils/displayOrder';
import { getMasterDataStatusLabel } from '../utils/masterDataStatus';
import { spreadMinutesFromDurations } from '../utils/workDuration';

type Envelope<T> = { success: boolean; message: string; data: T };
type ObjectValue = Record<string, unknown>;

const DETAILS_PAGE_SIZE = 100;

function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function requiredId(value: unknown, label: string): number {
  const parsed = numberOrNull(value);
  if (parsed === null || parsed <= 0) {
    throw Object.assign(new Error(`${label} is missing`), {
      code: 'VALIDATION_ERROR',
    });
  }
  return parsed;
}

function textOrNull(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function boolOrNull(value: unknown): boolean | null {
  if (value === true || value === 1 || value === '1') return true;
  if (value === false || value === 0 || value === '0') return false;
  return null;
}

function requiredBool(value: unknown, label: string): boolean {
  const parsed = boolOrNull(value);
  if (parsed === null) {
    throw Object.assign(new Error(`${label} is required`), {
      code: 'VALIDATION_ERROR',
    });
  }
  return parsed;
}

function emptyToNull(value: string | null | undefined): string | null {
  if (value === undefined || value === null) return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function nonNegativeOrZero(value: unknown): number {
  const parsed = numberOrNull(value);
  if (parsed === null || parsed < 0 || !Number.isInteger(parsed)) return 0;
  return parsed;
}

function normalizeListItem(row: ObjectValue): WorkPolicyListItem | null {
  try {
    const isActive = boolOrNull(row.isActive);
    return {
      workPolicyId: requiredId(
        row.workPolicyId ?? row.policyId,
        'workPolicyId',
      ),
      policyCode: textOrNull(row.policyCode) ?? String(row.workPolicyId),
      policyName: textOrNull(row.policyName) ?? 'Work policy',
      locationId: numberOrNull(row.locationId),
      locationName: textOrNull(row.locationName),
      defaultShiftId: numberOrNull(row.defaultShiftId),
      defaultShiftName: textOrNull(row.defaultShiftName ?? row.shiftName),
      expectedWorkMinutes: numberOrNull(row.expectedWorkMinutes),
      lunchBreakMinutes: numberOrNull(row.lunchBreakMinutes),
      spreadMinutes: numberOrNull(row.spreadMinutes),
      overtimeEnabled: boolOrNull(row.overtimeEnabled),
      isDefault: boolOrNull(row.isDefault),
      isActive,
      statusLabel: getMasterDataStatusLabel(
        isActive,
        textOrNull(row.statusCode),
      ),
      rowVersion: numberOrNull(row.rowVersion),
    };
  } catch {
    return null;
  }
}

function positiveOrFallback(value: unknown, fallback: number): number {
  const parsed = numberOrNull(value);
  return parsed != null && parsed > 0 ? parsed : fallback;
}

function positiveOrNull(value: unknown): number | null {
  const parsed = numberOrNull(value);
  return parsed != null && parsed > 0 ? parsed : null;
}

function resolveSpreadMinutes(
  row: ObjectValue,
  expectedWorkMinutes: number | null,
  lunchBreakMinutes: number,
): number | null {
  const stored = numberOrNull(row.spreadMinutes);
  if (stored != null && stored >= 0) return stored;
  if (expectedWorkMinutes == null) return null;
  return spreadMinutesFromDurations(expectedWorkMinutes, lunchBreakMinutes);
}

function normalizeDetails(row: ObjectValue): WorkPolicyDetails {
  const isActive = boolOrNull(row.isActive);
  const overtimeEnabled = boolOrNull(row.overtimeEnabled) ?? false;
  const workPolicyId = requiredId(row.workPolicyId ?? row.policyId, 'workPolicyId');
  const expectedWorkMinutes = positiveOrNull(row.expectedWorkMinutes);
  const lunchBreakMinutes = nonNegativeOrZero(row.lunchBreakMinutes);
  return {
    workPolicyId,
    policyCode: textOrNull(row.policyCode) ?? String(workPolicyId),
    policyName: textOrNull(row.policyName) ?? 'Work policy',
    description: textOrNull(row.description),
    locationId: numberOrNull(row.locationId),
    locationName: textOrNull(row.locationName),
    defaultShiftId: positiveOrFallback(
      row.defaultShiftId ?? row.shiftId,
      0,
    ),
    defaultShiftName: textOrNull(row.defaultShiftName ?? row.shiftName),
    holidayCalendarId: numberOrNull(row.holidayCalendarId),
    expectedWorkMinutes,
    lunchBreakMinutes,
    spreadMinutes: resolveSpreadMinutes(
      row,
      expectedWorkMinutes,
      lunchBreakMinutes,
    ),
    minimumHalfDayMinutes: positiveOrNull(row.minimumHalfDayMinutes),
    lateGraceMinutes: nonNegativeOrZero(row.lateGraceMinutes),
    earlyExitGraceMinutes: nonNegativeOrZero(row.earlyExitGraceMinutes),
    shortHoursToleranceMinutes: nonNegativeOrZero(
      row.shortHoursToleranceMinutes,
    ),
    maximumLateMinutes: nonNegativeOrZero(row.maximumLateMinutes),
    maximumEarlyExitMinutes: nonNegativeOrZero(row.maximumEarlyExitMinutes),
    workingDaysPerMonth: positiveOrNull(row.workingDaysPerMonth),
    allowMultiplePunches: boolOrNull(row.allowMultiplePunches) ?? false,
    allowManualAttendance: boolOrNull(row.allowManualAttendance) ?? false,
    allowAttendanceCorrection:
      boolOrNull(row.allowAttendanceCorrection) ?? false,
    wfhEligible: boolOrNull(row.wfhEligible) ?? false,
    permissionEnabled: boolOrNull(row.permissionEnabled) ?? false,
    overtimeEnabled,
    overtimeAfterMinutes: overtimeEnabled
      ? numberOrNull(row.overtimeAfterMinutes)
      : null,
    maximumOvertimeMinutes: overtimeEnabled
      ? numberOrNull(row.maximumOvertimeMinutes)
      : null,
    missingCheckoutPolicy: textOrNull(row.missingCheckoutPolicy),
    isDefault: boolOrNull(row.isDefault) ?? false,
    displayOrder: nonNegativeOrZero(row.displayOrder),
    isActive,
    statusLabel: getMasterDataStatusLabel(isActive, textOrNull(row.statusCode)),
    rowVersion: positiveOrFallback(row.rowVersion, 1),
  };
}

function buildListParams(
  page: number,
  pageSize: number,
  filters: WorkPolicyListFilters = {},
) {
  const params: Record<string, string | number | boolean> = {
    page,
    pageSize,
  };
  const search = filters.search?.trim();
  if (search) params.search = search;
  // Backend work-policy validator only accepts 1/0/'1'/'0' (not "true"/"false").
  if (filters.isActive === true) {
    params.isActive = 1;
  } else if (filters.isActive === false) {
    params.isActive = 0;
  }
  if (
    filters.locationId != null &&
    Number.isInteger(filters.locationId) &&
    filters.locationId > 0
  ) {
    params.locationId = filters.locationId;
  }
  return params;
}

export async function getWorkPolicies(
  options: {
    page?: number;
    pageSize?: number;
    filters?: WorkPolicyListFilters;
  } = {},
): Promise<WorkPolicySearchResult> {
  const page = options.page && options.page > 0 ? options.page : 1;
  const pageSize =
    options.pageSize && options.pageSize > 0 ? options.pageSize : 20;

  const response = await apiClient.get<
    Envelope<{
      items?: ObjectValue[];
      pagination?: {
        page?: number;
        pageSize?: number;
        totalRecords?: number;
        totalPages?: number;
      };
    }>
  >(API_ENDPOINTS.WORK_POLICIES.LIST, {
    params: buildListParams(page, pageSize, options.filters),
  });

  const data = response.data.data ?? {};
  const items = Array.isArray(data.items)
    ? data.items
        .map(normalizeListItem)
        .filter((item): item is WorkPolicyListItem => item != null)
    : [];
  const pagination = data.pagination ?? {};
  const totalItems = numberOrNull(pagination.totalRecords) ?? items.length;
  const resolvedPageSize = numberOrNull(pagination.pageSize) ?? pageSize;
  const totalPages =
    numberOrNull(pagination.totalPages) ??
    Math.max(1, Math.ceil(totalItems / resolvedPageSize));

  return {
    items,
    page: numberOrNull(pagination.page) ?? page,
    pageSize: resolvedPageSize,
    totalItems,
    totalPages,
  };
}

/**
 * No dedicated GET-by-id route. Locate the record through tenant-scoped search
 * pages (same approach the backend uses for ownership checks).
 */
export async function getWorkPolicyDetails(
  workPolicyId: number,
): Promise<WorkPolicyDetails> {
  const id = Number(workPolicyId);
  if (!Number.isInteger(id) || id <= 0) {
    throw Object.assign(new Error('workPolicyId must be a positive integer'), {
      code: 'VALIDATION_ERROR',
    });
  }

  let page = 1;
  let totalPages = 1;
  while (page <= totalPages) {
    const response = await apiClient.get<
      Envelope<{
        items?: ObjectValue[];
        pagination?: { totalPages?: number; totalRecords?: number };
      }>
    >(API_ENDPOINTS.WORK_POLICIES.LIST, {
      params: { page, pageSize: DETAILS_PAGE_SIZE },
    });
    const data = response.data.data ?? {};
    const items = Array.isArray(data.items) ? data.items : [];
    const match = items.find(
      row => numberOrNull(row.workPolicyId ?? row.policyId) === id,
    );
    if (match) return normalizeDetails(match);

    const totalRecords = numberOrNull(data.pagination?.totalRecords) ?? 0;
    totalPages =
      numberOrNull(data.pagination?.totalPages) ??
      Math.max(1, Math.ceil(totalRecords / DETAILS_PAGE_SIZE));
    page += 1;
  }

  throw Object.assign(new Error('Work policy was not found.'), {
    code: 'WORK_POLICY_NOT_FOUND',
  });
}

export async function createWorkPolicy(
  input: WorkPolicyCreateInput,
): Promise<WorkPolicyDetails> {
  const existing = await getWorkPolicies({
    page: 1,
    pageSize: DETAILS_PAGE_SIZE,
  });
  const body = {
    locationId: input.locationId ?? null,
    defaultShiftId: input.defaultShiftId,
    holidayCalendarId: input.holidayCalendarId ?? null,
    policyCode: input.policyCode.trim(),
    policyName: input.policyName.trim(),
    description: emptyToNull(input.description),
    expectedWorkMinutes: input.expectedWorkMinutes,
    lunchBreakMinutes: input.lunchBreakMinutes,
    spreadMinutes: spreadMinutesFromDurations(
      input.expectedWorkMinutes,
      input.lunchBreakMinutes,
    ),
    minimumHalfDayMinutes: input.minimumHalfDayMinutes,
    lateGraceMinutes: input.lateGraceMinutes,
    earlyExitGraceMinutes: input.earlyExitGraceMinutes,
    shortHoursToleranceMinutes: input.shortHoursToleranceMinutes,
    maximumLateMinutes: input.maximumLateMinutes,
    maximumEarlyExitMinutes: input.maximumEarlyExitMinutes,
    workingDaysPerMonth: input.workingDaysPerMonth,
    allowMultiplePunches: input.allowMultiplePunches,
    allowManualAttendance: input.allowManualAttendance,
    allowAttendanceCorrection: input.allowAttendanceCorrection,
    wfhEligible: input.wfhEligible,
    permissionEnabled: input.permissionEnabled,
    overtimeEnabled: input.overtimeEnabled,
    overtimeAfterMinutes: input.overtimeEnabled
      ? input.overtimeAfterMinutes ?? null
      : null,
    maximumOvertimeMinutes: input.overtimeEnabled
      ? input.maximumOvertimeMinutes ?? null
      : null,
    missingCheckoutPolicy: input.missingCheckoutPolicy.trim(),
    isDefault: input.isDefault,
    displayOrder: nextDisplayOrder([], existing.totalItems),
  };

  const response = await apiClient.post<Envelope<ObjectValue>>(
    API_ENDPOINTS.WORK_POLICIES.CREATE,
    body,
  );
  return normalizeDetails({
    ...body,
    isActive: true,
    rowVersion: 1,
    ...(response.data.data ?? {}),
  });
}

export async function updateWorkPolicy(
  workPolicyId: number,
  input: WorkPolicyUpdateInput,
): Promise<WorkPolicyDetails> {
  if (!Number.isInteger(workPolicyId) || workPolicyId <= 0) {
    throw Object.assign(new Error('workPolicyId must be a positive integer'), {
      code: 'VALIDATION_ERROR',
    });
  }

  const body = {
    locationId: input.locationId ?? null,
    defaultShiftId: input.defaultShiftId,
    holidayCalendarId: input.holidayCalendarId ?? null,
    policyName: input.policyName.trim(),
    description: emptyToNull(input.description),
    expectedWorkMinutes: input.expectedWorkMinutes,
    lunchBreakMinutes: input.lunchBreakMinutes,
    spreadMinutes: spreadMinutesFromDurations(
      input.expectedWorkMinutes,
      input.lunchBreakMinutes,
    ),
    minimumHalfDayMinutes: input.minimumHalfDayMinutes,
    lateGraceMinutes: input.lateGraceMinutes,
    earlyExitGraceMinutes: input.earlyExitGraceMinutes,
    shortHoursToleranceMinutes: input.shortHoursToleranceMinutes,
    maximumLateMinutes: input.maximumLateMinutes,
    maximumEarlyExitMinutes: input.maximumEarlyExitMinutes,
    workingDaysPerMonth: input.workingDaysPerMonth,
    allowMultiplePunches: input.allowMultiplePunches,
    allowManualAttendance: input.allowManualAttendance,
    allowAttendanceCorrection: input.allowAttendanceCorrection,
    wfhEligible: input.wfhEligible,
    permissionEnabled: input.permissionEnabled,
    overtimeEnabled: input.overtimeEnabled,
    overtimeAfterMinutes: input.overtimeEnabled
      ? input.overtimeAfterMinutes ?? null
      : null,
    maximumOvertimeMinutes: input.overtimeEnabled
      ? input.maximumOvertimeMinutes ?? null
      : null,
    missingCheckoutPolicy: input.missingCheckoutPolicy.trim(),
    isDefault: input.isDefault,
    displayOrder: input.displayOrder,
    isActive: requiredBool(input.isActive, 'isActive'),
    rowVersion: input.rowVersion,
  };

  const response = await apiClient.put<Envelope<ObjectValue>>(
    API_ENDPOINTS.WORK_POLICIES.UPDATE(workPolicyId),
    body,
  );
  return normalizeDetails({
    workPolicyId,
    policyCode: '—',
    ...body,
    ...(response.data.data ?? {}),
  });
}

export async function deactivateWorkPolicy(
  item: WorkPolicyDetails,
): Promise<WorkPolicyDetails> {
  // Never write a guessed value back to the policy.
  if (
    item.expectedWorkMinutes == null ||
    item.minimumHalfDayMinutes == null ||
    item.workingDaysPerMonth == null ||
    item.missingCheckoutPolicy == null
  ) {
    throw new Error(
      'Work policy details are not available. Reload the policy and try again.',
    );
  }
  return updateWorkPolicy(item.workPolicyId, {
    locationId: item.locationId,
    defaultShiftId: item.defaultShiftId,
    holidayCalendarId: item.holidayCalendarId,
    policyName: item.policyName,
    description: item.description,
    expectedWorkMinutes: item.expectedWorkMinutes,
    lunchBreakMinutes: item.lunchBreakMinutes,
    minimumHalfDayMinutes: item.minimumHalfDayMinutes,
    lateGraceMinutes: item.lateGraceMinutes,
    earlyExitGraceMinutes: item.earlyExitGraceMinutes,
    shortHoursToleranceMinutes: item.shortHoursToleranceMinutes,
    maximumLateMinutes: item.maximumLateMinutes,
    maximumEarlyExitMinutes: item.maximumEarlyExitMinutes,
    workingDaysPerMonth: item.workingDaysPerMonth,
    allowMultiplePunches: item.allowMultiplePunches,
    allowManualAttendance: item.allowManualAttendance,
    allowAttendanceCorrection: item.allowAttendanceCorrection,
    wfhEligible: item.wfhEligible,
    permissionEnabled: item.permissionEnabled,
    overtimeEnabled: item.overtimeEnabled,
    overtimeAfterMinutes: item.overtimeAfterMinutes,
    maximumOvertimeMinutes: item.maximumOvertimeMinutes,
    missingCheckoutPolicy: item.missingCheckoutPolicy,
    isDefault: item.isDefault,
    displayOrder: item.displayOrder,
    isActive: CONFIGURATION_RECORD_INACTIVE,
    rowVersion: item.rowVersion,
  });
}
